<script setup lang="ts">
/**
 * A thin bar across the top of the viewport while a navigation is pending: every route component is a lazy chunk,
 * and a first click on a sidebar item used to show nothing while it downloaded, so members clicked again.
 *
 * It only appears once a navigation has taken longer than `delay`, so the usual cached, instant route change
 * never flashes it. The view's own loading state (lazyView's skeleton) takes over once the route has resolved.
 */
import { onBeforeUnmount, ref } from "vue";
import { useRouter } from "vue-router";
import type { RouteLocationNormalized } from "vue-router";

const props = withDefaults( defineProps< {
  delay?: number;
} >(), {
  delay: 150
} );

const router = useRouter();
const visible = ref< boolean >( false );

// The navigation the bar belongs to. A navigation that a newer one cancelled still reaches afterEach, and must not
// hide the bar for the newer one; a guard redirect runs beforeEach again with its new target, so it takes over.
let pending: RouteLocationNormalized | null = null;
let showTimer: ReturnType< typeof setTimeout > | undefined;

function finish(): void {
  pending = null;
  clearTimeout( showTimer );
  showTimer = undefined;
  visible.value = false;
}

const removeBeforeEach = router.beforeEach( ( to ) => {
  pending = to;
  if ( !visible.value && showTimer === undefined ) {
    showTimer = setTimeout( () => {
      showTimer = undefined;
      visible.value = true;
    }, props.delay );
  }
} );

const removeAfterEach = router.afterEach( ( to ) => {
  if ( to === pending ) {
    finish();
  }
} );

// A thrown guard or a failed chunk download ends the navigation without afterEach. Reporting the error is
// router/navigation-errors.ts's job; this only hides the bar, and only for the navigation it belongs to.
const removeOnError = router.onError( ( _error, to ) => {
  if ( to === pending ) {
    finish();
  }
} );

onBeforeUnmount( () => {
  removeBeforeEach();
  removeAfterEach();
  removeOnError();
  finish();
} );
</script>

<template>
  <Transition name="route-progress">
    <div
      v-if="visible"
      role="progressbar"
      aria-label="Loading page"
      class="route-progress pointer-events-none fixed inset-x-0 z-[2000] h-0.5 overflow-hidden"
    >
      <div class="route-progress__bar h-full w-1/3 bg-primary"></div>
    </div>
  </Transition>
</template>

<style scoped>
.route-progress {
  /* Below the notch in a standalone PWA, where the private shell pads the same inset. */
  top: env(safe-area-inset-top, 0px);
}

.route-progress__bar {
  animation: route-progress-slide 1.1s ease-in-out infinite;
}

@keyframes route-progress-slide {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(300%);
  }
}

@media (prefers-reduced-motion: reduce) {
  /* index.css collapses every animation to one near-instant run, which would leave the bar off-screen. */
  .route-progress__bar {
    width: 100%;
    transform: none;
    animation: none;
  }
}

.route-progress-leave-active {
  transition: opacity 0.2s ease-out;
}

.route-progress-leave-to {
  opacity: 0;
}
</style>
